import { FServiceAPI, FUtil } from '@freelog/tools-lib';
import dayjs from 'dayjs';
import type {
  PodcastRssExistingResource,
  PodcastRssPreviewInfo,
  PodcastRssSubmitResult,
} from './types';

function maskPodcastEmail(email: string): string {
  const [name, domain] = email.split('@');
  if (!domain) {
    return email;
  }
  const visible = name.slice(0, Math.min(3, name.length));
  return `${visible}***@${domain}`;
}

function buildPodcastPreview(data: {
  feedData?: { channel?: Record<string, any> };
  audioItemCount?: number;
  itemCount?: number;
  matchedItemCount?: number;
}): PodcastRssPreviewInfo | undefined {
  const channel = data?.feedData?.channel;
  if (!channel) {
    return undefined;
  }
  return {
    title: channel.title || '',
    cover: channel.imageUrl || '',
    author: channel.author || '',
    episodeCount: data.audioItemCount ?? data.itemCount ?? 0,
    matchedItemCount: data.matchedItemCount,
    maskedEmail: channel.ownerEmail ? maskPodcastEmail(channel.ownerEmail) : undefined,
  };
}

async function fetchExistingResource(
  resourceId: string,
): Promise<PodcastRssExistingResource | undefined> {
  try {
    const { ret, errCode, data } = await FServiceAPI.Resource.info({
      resourceIdOrName: resourceId,
      isLoadPolicyInfo: 1,
    });
    if (ret !== 0 || errCode !== 0 || !data) {
      return undefined;
    }

    // 合集单品数量：Resource.info 不含 totalItem，需另查 catalogue items
    let collectionCount = 0;
    if (data.subjectType === 4) {
      try {
        const { data: batchData } = await FServiceAPI.Resource.batchResourceItems({
          resourceIds: resourceId,
          limit: 10,
          sortField: 'createDate',
          sortType: -1,
          isLoadItemResourceDetailInfo: 1,
          isLoadLatestVersionInfo: 1,
        });
        const matched = (batchData as { resourceId: string; totalItem: number }[] | undefined)?.find(
          (item) => item.resourceId === resourceId,
        );
        collectionCount = matched?.totalItem ?? 0;
      } catch {
        collectionCount = 0;
      }
    }

    return {
      id: data.resourceId,
      cover: data.coverImages?.[0] || '',
      name: data.resourceName || '',
      title: data.resourceTitle || '',
      version: data.latestVersion || '',
      policy: (data.policies || [])
        .filter((p: { status: number }) => p.status === 1)
        .map((p: { policyName: string }) => p.policyName),
      type: data.resourceType || [],
      status: data.status,
      updateDate: data.updateDate
        ? dayjs(data.updateDate).format(FUtil.Predefined.momentDateFormat)
        : '',
      username: data.username || '',
      useAvatar: FUtil.Tool.getAvatarUrl(data.userId),
      isChoice: data.operationType === 5,
      // subjectType 4 = 合集
      collection:
        data.subjectType === 4
          ? {
              status: data.latestVersion ? ('finish' as const) : ('unFinish' as const),
              count: collectionCount,
            }
          : null,
    };
  } catch {
    return undefined;
  }
}

/** 调用 RSS preview 并映射为组件所需的结果（含已占用 errorCode） */
export async function checkPodcastRssUrl(rssUrl: string): Promise<PodcastRssSubmitResult> {
  const { ret, errCode, data } = await FServiceAPI.Rss.bindingsPreview({
    feedUrl: rssUrl,
    isLoadItemData: 0,
  });

  if (ret !== 0 || errCode !== 0 || !data) {
    return { view: 'error_invalid' };
  }

  const podcast = buildPodcastPreview(data);
  const errorCode = data.errorCode as string | undefined;

  if (
    errorCode === 'submitpodcastwrss_error_alreadyexists' ||
    errorCode === 'submitpodcastwrss_error_alreadyexists02'
  ) {
    const existingResource = data.resourceId
      ? await fetchExistingResource(data.resourceId)
      : undefined;
    return {
      view:
        errorCode === 'submitpodcastwrss_error_alreadyexists'
          ? 'error_alreadyexists_self'
          : 'error_alreadyexists_other',
      podcast,
      existingResource,
    };
  }

  const channel = data.feedData?.channel;
  if (!channel) {
    return { view: 'error_invalid' };
  }
  if (!channel.ownerEmail) {
    return { view: 'error_noemail', podcast };
  }

  return {
    view: 'preview',
    podcast,
  };
}
